/**
 * Reads partner shares recorded only in the partner's guided-planning session (shares made before
 * sources carried `addedByClientId`). Guided planning and the self-funding details modal pass the
 * result to the funding-sources partner hooks so those old shares stay read-only for the recipient.
 * The D2C flow does not use it: it reads no guided session.
 */
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { selectClient } from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import type { LegacyPartnerSelection, LegacyPartnerSelections } from '@/features/funding-sources';
import {
  guidedPlanningApi,
  guidedPlanningSessionKeys,
} from '@/features/guided-financial-planning/api';

export function useLegacyPartnerAllocationSelections(): LegacyPartnerSelections {
  const client = useSelector(selectClient);
  const { flags } = useFeatureFlags();
  const partnerClientId =
    flags.partnerFundingSplit === true ? (client?.partnerClientId ?? null) : null;

  const { data: partnerSessions, isLoading } = useQuery({
    queryKey: guidedPlanningSessionKeys.list({ clientId: partnerClientId ?? '' }),
    queryFn: () => guidedPlanningApi.getSessions(partnerClientId!),
    enabled: !!partnerClientId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    selections: partnerSessions?.[0]?.stepData?.partnerAllocationSelections as
      | Record<string, LegacyPartnerSelection>
      | undefined,
    isLoading: !!partnerClientId && isLoading,
  };
}
